import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import MainLayout from "@/components/layout/MainLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Circle, Lock, RefreshCw, Loader2 } from "lucide-react";

type FulfillmentStage = {
  id: string;
  stage_type: string;
  order_number: number;
  title: string;
  description: string;
  estimated_duration_days: number;
};

type UserProgress = {
  current_stage: string;
  started_at: string;
  last_ontraport_sync: string | null;
  agreement_signed: boolean;
  typeform_submitted: boolean;
  current_stage_tags: string[] | null;
  onboarding_data: any;
};

export default function CustomerJourney({ embedded = false }: { embedded?: boolean }) {
  const [stages, setStages] = useState<FulfillmentStage[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  const [completedStages, setCompletedStages] = useState<string[]>([]);
  const [activeOrder, setActiveOrder] = useState<number>(1);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const initializeJourney = async () => {
      await fetchJourneyData();
      // Auto-sync silently with OntraPort on page load
      if (user) {
        handleSyncOntraPort(true); // true = silent mode
      }
    };
    
    initializeJourney();
  }, [user]);

  const fetchJourneyData = async () => {
    if (!user) return;

    try {
      // Fetch all stages
      const { data: stagesData, error: stagesError } = await supabase
        .from("fulfillment_stages")
        .select("*")
        .order("order_number");

      if (stagesError) throw stagesError;
      setStages(stagesData || []);

      // Fetch user progress
      const { data: progressData, error: progressError } = await supabase
        .from("user_fulfillment_progress")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (progressError && progressError.code !== "PGRST116") {
        throw progressError;
      }

      let currentProgress = progressData;

      // If no progress exists, create initial record
      if (!progressData) {
        const { data: newProgress, error: insertError } = await supabase
          .from("user_fulfillment_progress")
          .insert({
            user_id: user.id,
            current_stage: "UNCATEGORIZED",
          })
          .select()
          .single();

        if (insertError) throw insertError;
        currentProgress = newProgress;
        setUserProgress(newProgress);
      } else {
        setUserProgress(progressData);
      }

      // Calculate completed stages and determine active ("Up Next") stage
      if (currentProgress && stagesData) {
        const currentStageType = currentProgress.current_stage;
        let completed: string[] = [];
        
        const stageMatch = stagesData.find(s => s.stage_type === currentStageType);
        const currentStageOrder = stageMatch?.order_number || 0;
        const isCompletionStatus = currentStageType?.endsWith('_COMPLETE') || currentStageType === 'ONBOARDING_COMPLETE';
        
        // Mark all stages before the current stage as complete
        for (const stage of stagesData) {
          if (stage.order_number < currentStageOrder) {
            completed.push(stage.stage_type);
          }
          // Also mark Stage 2 as complete if agreement is signed
          if (stage.order_number === 2 && currentProgress.agreement_signed) {
            if (!completed.includes(stage.stage_type)) {
              completed.push(stage.stage_type);
            }
          }
        }
        
        // If the current status indicates completion of the matched stage, mark it complete too
        if (isCompletionStatus && currentStageOrder > 0) {
          const currentStage = stagesData.find(s => s.order_number === currentStageOrder);
          if (currentStage && !completed.includes(currentStage.stage_type)) {
            completed.push(currentStage.stage_type);
          }
        }
        
        // If onboarding fully complete, mark all as complete
        let nextActiveOrder = currentStageOrder > 0 ? currentStageOrder : 1;
        if (currentStageType === 'ONBOARDING_COMPLETE') {
          completed = stagesData.map(s => s.stage_type);
          nextActiveOrder = stagesData.length; // clamp to last stage
        } else if (isCompletionStatus) {
          nextActiveOrder = (currentStageOrder || 1) + 1;
        }
        
        // Special fallback: if we can't match the type but it's a completion status,
        // treat the first stage as completed and move to the second stage as active when possible
        if (!stageMatch && isCompletionStatus && currentStageType !== 'ONBOARDING_COMPLETE') {
          const firstStage = stagesData.find(s => s.order_number === 1);
          if (firstStage && !completed.includes(firstStage.stage_type)) {
            completed.push(firstStage.stage_type);
          }
          nextActiveOrder = 2;
        }
        
        // Clamp active order to valid range
        const clampedActiveOrder = Math.min(Math.max(1, nextActiveOrder), stagesData.length || 1);
        
        setCompletedStages(completed);
        setActiveOrder(clampedActiveOrder);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncOntraPort = async (silent = false) => {
    if (!user) return;

    setSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-ontraport', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.success) {
        // Only show success toast if not silent mode
        if (!silent) {
          toast({
            title: "Success",
            description: "Progress synced from OntraPort successfully!",
          });
        }
        await fetchJourneyData();
      } else if (data?.not_found) {
        // Contact not found in OntraPort - handle gracefully
        if (silent) {
          console.log('OntraPort contact not found - user may not be in system yet');
        } else {
          toast({
            title: "Not Yet Synced",
            description: data?.message || "Your account is not yet synced with OntraPort. Some features may be limited.",
          });
        }
      } else {
        // In silent mode, just log the error instead of showing toast
        if (silent) {
          console.log('Auto-sync failed:', data?.error || data?.message);
        } else {
          toast({
            title: "Update failed",
            description: data?.error || data?.message || "Could not update your account.",
            variant: "destructive",
          });
        }
      }
    } catch (error: any) {
      console.error('Sync error:', error);
      // Only show error toast if not in silent mode
      if (!silent) {
        toast({
          title: "Error",
          description: "Could not update your account. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setSyncing(false);
    }
  };

  const getStageStatus = (stage: FulfillmentStage) => {
    if (!userProgress) return "upcoming";
    
    // Completed if explicitly in completedStages or order is before the active one
    if (completedStages.includes(stage.stage_type) || stage.order_number < activeOrder) {
      return "completed";
    }
    // Current (Up Next) is the active order
    if (stage.order_number === activeOrder) {
      return "current";
    }
    return "upcoming";
  };

  const getTimeSinceSync = (syncTime: string) => {
    const now = new Date();
    const sync = new Date(syncTime);
    const diffMs = now.getTime() - sync.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  };

  const handleStageClick = (stage: FulfillmentStage) => {
    const status = getStageStatus(stage);
    if (status === "upcoming") {
      toast({
        title: "Stage Not Available",
        description: "Complete previous stages first.",
      });
      return;
    }
    navigate(`/journey/stage/${stage.stage_type.toLowerCase()}`);
  };

  if (loading) {
    if (embedded) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your journey...</p>
          </div>
        </div>
      );
    }
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading your journey...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const currentStepDisplay = Math.min(stages.length || 1, activeOrder || 1);
  const progressPercentage = stages.length > 0 ? Math.round((completedStages.length / stages.length) * 100) : 0;

  const content = (
    <div className={embedded ? "" : "container max-w-5xl py-8 px-4"}>
        <div className="mb-8">
          <div className="text-center mb-4">
            <h1 className="text-4xl font-bold mb-3 text-[#1a365d]">
              Your UNA Journey
            </h1>
            {userProgress?.onboarding_data?.legal_first_name && (
              <p className="text-lg mb-2">
                Welcome, {userProgress.onboarding_data.legal_first_name} {userProgress.onboarding_data.legal_last_name}!
              </p>
            )}
            <p className="text-lg text-muted-foreground">
              Track your progress through the UNA setup process
            </p>
            {userProgress?.last_ontraport_sync && (
              <p className="text-sm text-muted-foreground mt-2">
                Last updated: {getTimeSinceSync(userProgress.last_ontraport_sync)}
              </p>
            )}
          </div>
        </div>

        {/* Horizontal Progress Bar */}
        <div className="mb-12 bg-card rounded-lg p-6 shadow-sm border">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-[#1a365d]">
              Step {currentStepDisplay} of {stages.length} - {progressPercentage}% Complete
            </h2>
          </div>
          
          {/* Horizontal Step Indicator - Desktop */}
          <div className="relative overflow-x-auto pb-20 hidden md:block">
            <div className="flex items-center justify-between min-w-[600px] px-4">
              {stages.map((stage, index) => {
                const status = getStageStatus(stage);
                const isClickable = status !== "upcoming";
                
                return (
                  <div key={stage.id} className="flex items-center flex-1">
                    {/* Step Circle */}
                    <button
                      onClick={() => isClickable && handleStageClick(stage)}
                      disabled={!isClickable}
                      className={`relative flex flex-col items-center group ${
                        isClickable ? 'cursor-pointer' : 'cursor-not-allowed'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                          status === "completed"
                            ? "bg-[#4CAF50] border-[#4CAF50] text-white"
                            : status === "current"
                            ? "bg-[#D4AF37] border-[#D4AF37] text-white"
                            : "bg-background border-[#E0E0E0] text-[#E0E0E0]"
                        } ${isClickable ? 'group-hover:scale-110' : ''}`}
                      >
                        {status === "completed" ? (
                          <Check className="w-5 h-5" />
                        ) : (
                          <span className="text-sm font-semibold">{stage.order_number}</span>
                        )}
                      </div>
                      
                      {/* Step Label */}
                      <div className="absolute top-12 left-1/2 -translate-x-1/2 text-center whitespace-nowrap">
                        <p className={`text-xs font-medium ${
                          status === "current" ? "text-[#D4AF37]" : "text-muted-foreground"
                        }`}>
                          {status === "current" ? "Up Next" : stage.order_number}
                        </p>
                      </div>
                    </button>
                    
                    {/* Connecting Line */}
                    {index < stages.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 ${
                          completedStages.includes(stages[index + 1].stage_type)
                            ? "bg-[#4CAF50]"
                            : "border-t-2 border-dashed border-[#E0E0E0]"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Step Indicator */}
          <div className="md:hidden pb-4">
            <div className="flex items-center justify-center gap-2 mb-3">
              {stages.map((stage, index) => {
                const status = getStageStatus(stage);
                return (
                  <div
                    key={stage.id}
                    className={`w-3 h-3 rounded-full ${
                      status === "completed"
                        ? "bg-[#4CAF50]"
                        : status === "current"
                        ? "bg-[#D4AF37]"
                        : "bg-muted"
                    }`}
                  />
                );
              })}
            </div>
            <p className="text-center text-sm text-muted-foreground">
              Step {currentStepDisplay} of {stages.length}
            </p>
          </div>
        </div>

        {/* Stages Timeline */}
        <div className="space-y-6">
          {stages.map((stage, index) => {
            const status = getStageStatus(stage);
            const isClickable = status !== "upcoming";

            return (
              <Card
                key={stage.id}
                className={`relative transition-all duration-300 ${
                  isClickable
                    ? "cursor-pointer hover:shadow-lg hover:scale-[1.02]"
                    : "opacity-60"
                } ${
                  status === "current"
                    ? "border-[#D4AF37] border-2 shadow-md"
                    : ""
                }`}
                onClick={() => handleStageClick(stage)}
              >
                <div className="p-6 flex items-start gap-4">
                  {/* Stage Icon */}
                  <div className="flex-shrink-0">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        status === "completed"
                          ? "bg-[#4CAF50] text-white"
                          : status === "current"
                          ? "bg-[#D4AF37] text-white animate-pulse"
                          : "bg-secondary text-muted-foreground"
                      }`}
                    >
                      {status === "completed" ? (
                        <Check className="w-6 h-6" />
                      ) : status === "current" ? (
                        <Circle className="w-6 h-6" />
                      ) : (
                        <Circle className="w-6 h-6" />
                      )}
                    </div>
                  </div>

                  {/* Stage Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-muted-foreground">
                            Stage {stage.order_number}
                          </span>
                          {status === "current" && (
                            <span className="text-xs font-bold text-[#D4AF37] bg-[#D4AF37]/10 px-2 py-1 rounded-full">
                              UP NEXT
                            </span>
                          )}
                        </div>
                        <h3 className="text-xl font-bold text-[#1a365d] mb-1">
                          {stage.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-3">
                      {stage.description}
                    </p>
                    <div className="flex items-center gap-4 text-sm">
                      {isClickable && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-[#c9a961] hover:text-[#1a365d]"
                        >
                          View Details →
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Connector Line */}
                {index < stages.length - 1 && (
                  <div
                    className={`absolute left-[2.75rem] top-[5.5rem] w-0.5 h-6 ${
                      status === "completed" ? "bg-[#1a365d]" : "bg-secondary"
                    }`}
                  />
                )}
              </Card>
            );
          })}
        </div>
      </div>
  );

  if (embedded) {
    return content;
  }

  return <MainLayout>{content}</MainLayout>;
}
